import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts';
import { Box, Link, Paper, Stack, Typography } from '@mui/material';
import { Help, InfoOutlined, WarningAmber } from '@mui/icons-material';
import {
	DataGrid,
	GridRowSelectionModel,
	MiniLoading,
	Text,
	rem,
	error,
	getTenantId,
	GridLogicOperator,
	GridFilterModel,
	useIsFeatureEnabled,
} from '@innovyze/stylovyze';

import { CustomDataGridHeader } from '@Components/Utils/CustomDataGridHeader';
import { DarkInfoTooltip } from '@Components/deteriorationModel/configurationTab/DarkInfoTooltip';

import { listDeteriorationModelGroupsResultsAction, setPaginationState } from '@Actions';
import { useGlobalization } from '@Translations';
import {
	GetDeteriorationModelGroupsResultsPaginatedRequests,
	CurveMethod,
	WeibullDeclineReason,
} from '@Types/deteriorationModels.types';
import {
	selectDeteriorationModel,
	selectDeteriorationModelGroupsResults,
	selectModelPaginationState,
} from '@Selectors';
import {
	getDeteriorationModelGroupsResultsChart,
	groupsResultParamsMapping,
} from '@Services/deteriorationModels.services';
import { renderHeaderWithTooltip } from '../utils/renderHeaderWithTooltip';
import { InfoTooltip } from '../utils/InfoTooltip';
import {
	WeibullFallbackBanner,
	SEVERITY_PALETTE,
	type WeibullFallbackSeverity,
} from '@Components/deteriorationModel/utils/WeibullFallbackBanner';
import { getWeibullFallbackSignal } from '../utils/weibullFallback';
import { getWeibullDeclineNote, getWeibullDeclineSeverity } from '@Components/deteriorationModel/utils/weibullDecline';
import { WeibullFitNotes } from '../utils/WeibullFitNotes';
import { isFlatSeries, WeibullParams } from '../utils/weibullFit';
import { baseChartOptions } from '@Components/deteriorationModel/utils/chartDefaults';

/**
 * The tone one row's fallback earns. The chart-card banner and the per-row icon must agree, so
 * both read this: a `not-applicable` fallback is never informational, because the cell renders it
 * amber with its own wording and never consults a decline reason at all.
 */
const rowFallbackSeverity = (row: GroupResultRow): WeibullFallbackSeverity => {
	const signal = getWeibullFallbackSignal({ fallback: row.fallback, deteriorationMethod: row.deteriorationMethod });
	if (signal?.variant !== 'fit-unavailable') return 'warning';
	return getWeibullDeclineSeverity(row.fallbackReason) ?? 'warning';
};

const curveMethods = [
	{
		label: 'Undefined',
		value: CurveMethod.UNDEFINED,
	},
	{
		label: 'From Failure Definition',
		value: CurveMethod.FAILURE_DEFINITION,
	},
	{
		label: 'Custom Curve',
		value: CurveMethod.CUSTOM_CURVE,
	},
	{
		label: 'Industry Standard Curve',
		value: CurveMethod.INDUSTRY_CURVE,
	},
	{
		label: 'Copy Curve from Another Group',
		value: CurveMethod.COPIED_CURVE,
	},
	{
		label: 'One Expected Useful Life',
		value: CurveMethod.ONE_EXPECTED_LIFE,
	},
];

function createTooltipFormatter(t: (key: string) => string): Highcharts.TooltipFormatterCallbackFunction {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	return function (this: any) {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const dataPoints = (this.points ?? []).filter((p: any) => p.series.name !== 'Failure Threshold');
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const hasThreshold = (this.points ?? []).some((p: any) => p.series.name === 'Failure Threshold');
		let html = `<b>${t('Age')}: ${this.x} ${t('yr')}</b><br/>`;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		dataPoints.forEach((p: any) => {
			html += `<span style="color:${p.series.color}">●</span> <b>${p.series.name}</b>: ${(p.y ?? 0).toFixed(1)}%<br/>`;
		});
		if (hasThreshold) {
			html +=
				'<span style="color:#ef4444">- -</span> ' +
				`<span style="color:#6b7280;font-size:11px">${t('Failure Threshold')}: 80%</span>`;
		}
		return html;
	};
}

interface GroupsResultsProps {
	onShowAssetsForGroup?: (groupName: string) => void;
}

interface GroupResultRow {
	id: string;
	assetGroupName?: string;
	fallback?: boolean;
	// Why the 3-parameter fit was declined, when the run recorded one. Undefined for runs from
	// before I360A-2882, which is why the tooltip still needs its generic wording.
	fallbackReason?: WeibullDeclineReason;
	deteriorationMethod: CurveMethod;
	weibullParams?: WeibullParams;
}

export const GroupsResults = ({ onShowAssetsForGroup }: GroupsResultsProps) => {
	const { t } = useGlobalization();
	const failuresLabel = t('Assets with Failures');
	const failuresHeading = t('Assets with Failures');
	const failuresContent = t(
		'The number of assets in this group with a recorded failure matching the Failure Definition. Only applicable for groups using the From Failure Definition survival curve method.',
	);
	const thresholdAgeLabel = t('Age at 80% Failure Prob. (Years)');
	const thresholdAgeHeading = t('Threshold Age (80%)');
	const thresholdAgeContent = t(
		'The age at which the group survival curve crosses the 80% failure probability threshold — equivalent to the group-level RUL',
	);
	const dispatch = useDispatch();
	const isWeibull3ParamEnabled = useIsFeatureEnabled('info360-weibull-3-param');

	const deteriorationModel = selectDeteriorationModel();
	const currentDeteriorationModel = deteriorationModel.deteriorationModel;
	const modelId = currentDeteriorationModel?.id || '';
	const runId = currentDeteriorationModel?.latestRunResultsId || '';

	const storedPaginationState = selectModelPaginationState(modelId);
	const defaultRequest: GetDeteriorationModelGroupsResultsPaginatedRequests = {
		modelId,
		runId,
		page: 1,
		slice: 10,
		sortKey: 'assetGroupName',
		sortDescending: true,
		searchValue: null,
		filter: null,
		filterModel: { items: [] } as GridFilterModel,
	};

	const [groupsResultsRequestState, setGroupsResultsRequestState] =
		useState<GetDeteriorationModelGroupsResultsPaginatedRequests>(() => {
			const storedRequest = storedPaginationState?.results.groups;
			if (storedRequest) {
				return {
					...storedRequest,
					modelId,
					runId,
				};
			}
			return defaultRequest;
		});
	// The request state is seeded once, on mount. Overlay the model's current ids so
	// a re-run (a new latestRunResultsId while this stays mounted) requests the new
	// run's results rather than the run that was current when this mounted.
	const groupsResultsRequest = useMemo<GetDeteriorationModelGroupsResultsPaginatedRequests>(
		() => ({ ...groupsResultsRequestState, modelId, runId }),
		[groupsResultsRequestState, modelId, runId],
	);

	const updateGroupsResultsRequest = (
		updater: (
			prev: GetDeteriorationModelGroupsResultsPaginatedRequests,
		) => GetDeteriorationModelGroupsResultsPaginatedRequests,
	) => {
		setGroupsResultsRequestState(prev => {
			const next = updater(prev);
			dispatch(
				setPaginationState({
					modelId,
					tab: 'groups',
					paginationRequest: next,
				}),
			);
			return next;
		});
	};
	const groupsResults = selectDeteriorationModelGroupsResults();

	const [selectedGroups, setSelectedGroups] = useState<GridRowSelectionModel>([]);

	// Track chart state per asset group
	const [chartDataMap, setChartDataMap] = useState<{
		[assetGroupId: string]: {
			data: { age: number; failureProbability: number }[] | null;
			isLoading: boolean;
			error: string | null;
		};
	}>({});

	useEffect(() => {
		dispatch(listDeteriorationModelGroupsResultsAction(groupsResultsRequest));
	}, [groupsResultsRequest, currentDeteriorationModel?.latestRunResultsId]);

	// Fetch chart data only for newly selected groups
	useEffect(() => {
		const fetchChartData = async () => {
			const currentSelectedIds = selectedGroups.map(id => id.toString());
			const currentlyLoadedIds = Object.keys(chartDataMap);

			// Find newly selected groups that don't have data yet
			const newlySelectedIds = currentSelectedIds.filter(id => !currentlyLoadedIds.includes(id));

			// Remove data for deselected groups
			const deselectedIds = currentlyLoadedIds.filter(id => !currentSelectedIds.includes(id));

			if (deselectedIds.length > 0) {
				setChartDataMap(prevMap => {
					const newMap = { ...prevMap };
					deselectedIds.forEach(id => {
						delete newMap[id];
					});
					return newMap;
				});
			}

			// Fetch data for newly selected groups
			if (newlySelectedIds.length > 0) {
				// Set loading state for newly selected groups
				setChartDataMap(prevMap => ({
					...prevMap,
					...newlySelectedIds.reduce(
						(acc, id) => ({
							...acc,
							[id]: { data: null, isLoading: true, error: null },
						}),
						{},
					),
				}));

				// Fetch data for each newly selected group individually
				const fetchPromises = newlySelectedIds.map(async id => {
					try {
						const response = await getDeteriorationModelGroupsResultsChart(
							groupsResultsRequest.modelId,
							groupsResultsRequest.runId,
							id,
						);

						setChartDataMap(prevMap => ({
							...prevMap,
							[id]: {
								data: response.data.data.inference_results.map(result => ({
									age: result.age,
									failureProbability: result.failure_probability * 100,
								})),
								isLoading: false,
								error: null,
							},
						}));
					} catch (err) {
						console.error(`Error fetching chart data for group ${id}:`, err);
						setChartDataMap(prevMap => ({
							...prevMap,
							[id]: { data: null, isLoading: false, error: err as string },
						}));
						dispatch(error(t('Failed to load chart data for selected group.')));
					}
				});

				// Wait for all requests to complete
				await Promise.allSettled(fetchPromises);
			}
		};

		fetchChartData();
	}, [selectedGroups, chartDataMap]);

	// Get chart data from the map
	const getChartData = () => {
		const validEntries = Object.entries(chartDataMap).filter(([, state]) => state.data !== null);
		return validEntries.reduce(
			(acc, [id, state]) => ({
				...acc,
				[id]: state.data || [],
			}),
			{} as Record<string, { age: number; failureProbability: number }[]>,
		);
	};

	// Create chart options based on accumulated data
	const chartData = getChartData();
	const maxAge = Object.values(chartData).reduce((max, points) => {
		const seriesMax = points.reduce((m, p) => Math.max(m, p.age), 0);
		return Math.max(max, seriesMax);
	}, 0);

	const thresholdSeries: Highcharts.SeriesLineOptions[] =
		maxAge > 0
			? [
					{
						name: 'Failure Threshold',
						type: 'line',
						color: '#ef4444',
						dashStyle: 'Dash',
						lineWidth: 2,
						marker: { enabled: false },
						showInLegend: false,
						enableMouseTracking: true,
						data: Array.from({ length: maxAge + 1 }, (_, i): [number, number] => [i, 80]),
						states: { hover: { lineWidth: 2, halo: { size: 0 } } },
					},
			  ]
			: [];

	const chartOptions: Highcharts.Options = {
		...baseChartOptions,
		chart: { type: 'line', height: 400 },
		title: { text: '' },
		xAxis: { title: { text: t('Age (Years)') } },
		yAxis: {
			title: { text: t('Cumulative Failure Probability (%)') },
			min: 0,
			max: 100,
		},
		legend: { enabled: true },
		tooltip: {
			shared: true,
			formatter: createTooltipFormatter(t),
		},
		series: [
			...Object.entries(chartData).map(([id, data]) => {
				const groupRow = groupsResults.rows.find(row => row.id === id);
				const groupName = groupRow?.assetGroupName || `Group ${id}`;
				return {
					name: groupName,
					data: data.map(point => [point.age, point.failureProbability]),
					type: 'line' as const,
				};
			}),
			...thresholdSeries,
		],
		plotOptions: { line: { marker: { enabled: true } } },
	};

	const handleRefresh = () => {
		dispatch(listDeteriorationModelGroupsResultsAction(groupsResultsRequest));
	};

	const seriesKey = Object.keys(chartData)
		.sort((a, b) => a.localeCompare(b))
		.join(',');

	const selectedGroupIds = new Set(selectedGroups.map(id => id.toString()));
	const selectedRowsWithFallback = isWeibull3ParamEnabled
		? (groupsResults.rows as unknown as GroupResultRow[]).filter(
				row =>
					selectedGroupIds.has(row.id) &&
					getWeibullFallbackSignal({ fallback: row.fallback, deteriorationMethod: row.deteriorationMethod }),
		  )
		: [];
	const showChartFallbackBanner = selectedRowsWithFallback.length > 0;
	// The banner summarises several groups at once, so it can only drop the warning when nothing in
	// any of them warrants one -- and it has to agree with the icon on every row it summarises, or
	// the card says "nothing is wrong" directly above something flagged amber. `every` on an empty
	// array is `true`, so the length check carries real weight rather than just guarding the render.
	const chartFallbackSeverity =
		showChartFallbackBanner && selectedRowsWithFallback.every(row => rowFallbackSeverity(row) === 'info')
			? 'info'
			: 'warning';

	// Notes for each charted group: the coefficients its curve was produced from, and a warning if
	// that curve never moves. Flatness is read from the fetched curve rather than inferred from the
	// coefficients, so it needs no threshold on what counts as an implausible fit.
	const chartedGroupNotes = (groupsResults.rows as unknown as GroupResultRow[])
		.filter(row => selectedGroupIds.has(row.id))
		.map(row => ({
			id: row.id,
			label: row.assetGroupName,
			params: row.weibullParams,
			isFlat: isFlatSeries((chartDataMap[row.id]?.data ?? []).map(point => point.failureProbability)),
		}))
		.filter(note => note.isFlat || note.params !== undefined);

	return (
		<Stack direction="column" spacing={2}>
			<Paper elevation={2} sx={{ padding: rem(16), minHeight: rem(450) }}>
				{showChartFallbackBanner && (
					<Box mb={1}>
						<WeibullFallbackBanner
							severity={chartFallbackSeverity}
							message={t(
								'One or more selected asset groups ran as 2-parameter Weibull. See the flagged Survival Curve Method in the table below.',
							)}
						/>
					</Box>
				)}
				<Box display="flex" alignItems="center" gap={0.5} mb={1}>
					<Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#272833' }}>
						{t('Cumulative Failure Probability by Age')}
					</Typography>
					<InfoTooltip
						heading={t('Cumulative Failure Probability')}
						content={[
							t('How quickly do assets degrade? Steeper curve = faster degradation, shorter lifespan.'),
							t(
								'Use this to: Compare degradation rates between groups and find where the curve crosses 80% Failure Probability.',
							),
						]}>
						<Help sx={{ fontSize: '1rem', color: '#9ca3af', cursor: 'pointer' }} />
					</InfoTooltip>
				</Box>
				{Object.keys(chartData).length === 0 ? (
					<Box display="flex" justifyContent="center" alignItems="center" minHeight={rem(400)}>
						<Text>{t('Select asset groups to view their survival probability charts')}</Text>
					</Box>
				) : (
					<HighchartsReact
						key={seriesKey}
						highcharts={Highcharts}
						options={chartOptions}
						containerProps={{ style: { width: '100%' } }}
					/>
				)}
				{chartedGroupNotes.map(note => (
					<WeibullFitNotes key={note.id} isFlat={note.isFlat} params={note.params} label={note.label} />
				))}
			</Paper>
			<DataGrid
				weave
				// loading
				loading={groupsResults.isLoading}
				skeletonRowCount={groupsResults.pagination.slice}
				// columns and headers
				columns={[
					{
						field: 'assetGroupName',
						headerName: t('Asset Group Name'),
						renderHeader: () => <Text style={{ fontWeight: 'bold' }}>{t('Asset Group Name')}</Text>,
						renderCell: params => {
							const isLoading = chartDataMap[params.row.id]?.isLoading || false;
							return (
								<Box display="flex" alignItems="center" gap={1}>
									<Text>{params.value}</Text>
									{isLoading && <MiniLoading loading={isLoading} />}
								</Box>
							);
						},
						flex: 1,
						type: 'string',
						filterable: true,
						align: 'left',
						headerAlign: 'left',
					},
					{
						field: 'assetGroupDescription',
						headerName: t('Group Description'),
						renderHeader: () => <Text style={{ fontWeight: 'bold' }}>{t('Description')}</Text>,
						flex: 1,
						type: 'string',
						filterable: true,
						align: 'left',
						headerAlign: 'left',
					},
					{
						field: 'assetCount',
						headerName: t('Asset Count'),
						renderHeader: () => <Text style={{ fontWeight: 'bold' }}>{t('Asset Count')}</Text>,
						flex: 1,
						type: 'number',
						filterable: true,
						align: 'left',
						headerAlign: 'left',
					},
					{
						field: 'failureCount',
						headerName: failuresLabel,
						renderHeader: () => renderHeaderWithTooltip(failuresLabel, failuresHeading, failuresContent),
						flex: 1,
						type: 'number',
						filterable: true,
						align: 'left',
						headerAlign: 'left',
						renderCell: params => {
							// "Assets with Failures" only applies to groups using the Failure Definition method.
							if (params.row.deteriorationMethod !== CurveMethod.FAILURE_DEFINITION || params.value == null) {
								return <span style={{ color: '#9ca3af' }}>-</span>;
							}
							return params.value;
						},
					},
					{
						field: 'deteriorationMethod',
						headerName: t('Survival Curve Method'),
						renderHeader: () => <Text style={{ fontWeight: 'bold' }}>{t('Survival Curve Method')}</Text>,
						flex: 1,
						type: 'singleSelect',
						valueOptions: curveMethods,
						filterable: true,
						align: 'left',
						headerAlign: 'left',
						renderCell: params => {
							const methodLabel = curveMethods.find(m => m.value === params.value)?.label ?? '';
							const signal = isWeibull3ParamEnabled
								? getWeibullFallbackSignal({
										fallback: params.row.fallback,
										deteriorationMethod: params.row.deteriorationMethod,
								  })
								: null;
							// Shared with the configuration-tab banner so a user moving between the tabs meets the
							// same explanation. Null for a run from before I360A-2882, or for a reason this build
							// does not recognise — both keep the generic wording below.
							const declineNote =
								signal?.variant === 'fit-unavailable' ? getWeibullDeclineNote(params.row.fallbackReason, t) : null;
							// The heading already states that the run fell back, so the body carries only the reason.
							const body =
								signal?.variant === 'not-applicable'
									? t('Ran as 2-parameter Weibull — 3-parameter is not applicable for this curve method.')
									: declineNote?.message ??
									  t(
											// eslint-disable-next-line quotes
											"Ran as 2-parameter Weibull — this asset group's failure data did not support a distinct 3-parameter fit.",
									  );
							// `no_minimum_life` is a statement about the data rather than a problem, so it is not
							// flagged amber here either. Read through the same helper the chart-card banner uses, so
							// the two cannot disagree about which rows are harmless.
							const isInfo = rowFallbackSeverity(params.row) === 'info';
							const FallbackIcon = isInfo ? InfoOutlined : WarningAmber;
							return (
								<Box display="flex" alignItems="center" gap={0.5}>
									<Text>{t(methodLabel)}</Text>
									{signal && (
										<DarkInfoTooltip heading={t('Ran as 2-parameter Weibull')} body={body}>
											<FallbackIcon
												data-testid="weibull-fallback-icon"
												data-severity={isInfo ? 'info' : 'warning'}
												sx={{
													fontSize: 16,
													color: SEVERITY_PALETTE[isInfo ? 'info' : 'warning'].accent,
													cursor: 'pointer',
												}}
											/>
										</DarkInfoTooltip>
									)}
								</Box>
							);
						},
					},
					{
						field: 'thresholdAge',
						headerName: thresholdAgeLabel,
						renderHeader: () => renderHeaderWithTooltip(thresholdAgeLabel, thresholdAgeHeading, thresholdAgeContent),
						flex: 1,
						type: 'number',
						filterable: true,
						align: 'left',
						headerAlign: 'left',
						renderCell: params => {
							// Render gray "-" when the group's curve never reaches the 80% threshold.
							if (params.value == null) return <span style={{ color: '#9ca3af' }}>-</span>;
							return (params.value as number).toFixed(2);
						},
					},
					...(onShowAssetsForGroup
						? [
								{
									field: 'showAssets',
									headerName: '',
									renderHeader: () => <></>,
									renderCell: (params: { row: { assetGroupName: string } }) => (
										<Link
											component="button"
											underline="hover"
											sx={{ fontSize: '0.8125rem', cursor: 'pointer', whiteSpace: 'nowrap' }}
											onClick={() => onShowAssetsForGroup(params.row.assetGroupName)}>
											{t('Show assets')}
										</Link>
									),
									width: 105,
									sortable: false,
									filterable: false,
									align: 'left' as const,
									headerAlign: 'left' as const,
								},
						  ]
						: []),
				]}
				disableColumnMenu
				disableColumnPinning
				disableColumnReorder
				// footer
				footerHelper={t('{{from}}-{{to}} of {{total}}', {
					from: (groupsResults.pagination.page - 1) * groupsResults.pagination.slice + 1,
					to: Math.min(groupsResults.pagination.page * groupsResults.pagination.slice, groupsResults.pagination.total),
					total: groupsResults.pagination.total,
				})}
				// toolbar actions
				disableRowSelectionOnClick
				// filter
				filterMode="server"
				filterModel={groupsResultsRequest.filterModel as GridFilterModel}
				onFilterModelChange={model => {
					const hasItems = !!model.items && model.items.length > 0;

					const filter: NonNullable<GetDeteriorationModelGroupsResultsPaginatedRequests['filter']> = {};

					if (hasItems) {
						model.items.forEach(item => {
							if (!item.field) return;
							const entry = {
								operator: item.operator,
								value: item.value ?? null,
							};
							if (item.field === 'assetGroupName') filter.assetGroupName = entry;
							else if (item.field === 'assetGroupDescription') filter.assetGroupDescription = entry;
							else if (item.field === 'assetCount') filter.assetCount = entry;
							else if (item.field === 'failureCount') filter.failureCount = entry;
							else if (item.field === 'deteriorationMethod') filter.deteriorationMethod = entry;
							else if (item.field === 'thresholdAge') filter.thresholdAge = entry;
						});
					}

					updateGroupsResultsRequest(prev => ({
						...prev,
						filter: hasItems && Object.keys(filter).length > 0 ? filter : null,
						filterModel: model,
						page: 1,
					}));
				}}
				slots={{ toolbar: CustomDataGridHeader }}
				slotProps={{
					toolbar: {
						search: {
							value: groupsResultsRequest.searchValue || '',
							onChange: (searchValue: string) => {
								updateGroupsResultsRequest(prev => ({
									...prev,
									searchValue,
								}));
							},
							onCancelSearch: () => {
								updateGroupsResultsRequest(prev => ({
									...prev,
									searchValue: null,
								}));
							},
							placeholder: t('Search by Asset Group Name'),
						},
						filterToolbar: true,
						refreshButton: {
							onRefresh: handleRefresh,
						},
						exportProps: {
							source: 'pipe.deterioration.export.create',
							total: groupsResults.pagination.total,
							requestParams: {
								...(groupsResultParamsMapping(groupsResultsRequest, true) as Record<string, string>),
								export_type: 'pipe-deterioration-export',
								results_type: 'groups',
								model_id: currentDeteriorationModel?.id || '',
								run_results_id: currentDeteriorationModel?.latestRunResultsId || '',
								tenant_id: getTenantId() || '',
							},
						},
						weave: true,
						loading: groupsResults.isLoading,
					},
					filterPanel: {
						logicOperators: [GridLogicOperator.And, GridLogicOperator.Or],
					},
				}}
				// sorting
				sortingMode="server"
				sortModel={[
					{
						field: groupsResultsRequest.sortKey,
						sort: groupsResultsRequest.sortDescending ? 'desc' : 'asc',
					},
				]}
				onSortModelChange={model => {
					const sortKey = model[0]?.field || 'assetGroupName';
					const sortDescending = model[0]?.sort === 'desc';
					updateGroupsResultsRequest(prev => ({
						...prev,
						sortKey,
						sortDescending,
					}));
				}}
				// rows
				rows={groupsResults.rows}
				rowCount={groupsResults.pagination.total}
				checkboxSelection
				onRowSelectionModelChange={selectedRows => setSelectedGroups(selectedRows)}
				// pagination
				pagination
				pageSizeOptions={[5, 10, 15, 25, 50, 100]}
				paginationMode="server"
				paginationModel={{
					page: groupsResults.pagination.page - 1,
					pageSize: groupsResults.pagination.slice,
				}}
				onPaginationModelChange={model => {
					updateGroupsResultsRequest(prev => ({
						...prev,
						page: model.page + 1,
						slice: model.pageSize,
					}));
				}}
			/>
		</Stack>
	);
};
